import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RowCard } from '../patterns';
import type { NutritionSummary } from '../../services/nutritionService';

/**
 * `data.count` sums the foliar and soil outstanding sessions, but `data.rows`
 * is foliar-only (nutritionDashboardService.js:145-147). A soil-first tenant
 * therefore read "Sign-offs outstanding: 3" on the KPI tile directly above
 * "Nothing awaiting sign-off." here — two answers to one question on one
 * screen, and no route to the soil sessions from this page.
 *
 * `soilOutstanding` is `summary.soil.outstanding`. It is NOT folded into
 * `rows`: that row shape carries a foliar roundName/orchardNo that soil
 * sessions do not have. The card instead states the soil count and links to
 * the soil sign-off page, and the "nothing awaiting" empty state now requires
 * BOTH halves to be zero.
 */
export const NutritionSignoffCard: React.FC<{
  data: NutritionSummary['signoffsOutstanding'];
  soilOutstanding?: number;
}> = ({ data, soilOutstanding = 0 }) => {
  const { t } = useTranslation();
  return (
    <section className="ntr-dash-card" data-testid="nutrition-signoff-card">
      <header className="ntr-dash-card__head">
        <h3 className="ntr-dash-card__title">{t('nutrition.dashboard.signoff.title')}</h3>
        <Link to="/nutrition/foliar" className="ntr-dash-card__viewall">
          {t('nutrition.dashboard.signoff.viewAll')}
        </Link>
      </header>
      <div className="ntr-dash-card__body">
        {data.rows.length === 0 && soilOutstanding === 0 && (
          <p className="ntr-dash-card__empty">{t('nutrition.dashboard.signoff.empty')}</p>
        )}
        {soilOutstanding > 0 && (
          <p className="ntr-dash-card__note" data-testid="signoff-soil-note">
            {t('nutrition.dashboard.signoff.soilNote', { count: soilOutstanding })}{' '}
            <Link to="/nutrition/soil/sign-off">
              {t('nutrition.dashboard.signoff.soilViewAll')}
            </Link>
          </p>
        )}
        {data.rows.map((r) => (
          <RowCard
            key={r.sessionId}
            testId={`signoff-row-${r.sessionId}`}
            icon="bi-pencil-square"
            title={r.orchardNo}
            subtitle={t('nutrition.dashboard.signoff.subtitle', {
              round: r.roundName,
              season: r.season ?? '',
            })}
          />
        ))}
      </div>
    </section>
  );
};

export default NutritionSignoffCard;
